import { useBankProfile } from '@contexts/BankProfileContext';
import apiClient from '@services/apiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type SigningTarget =
	| { type: 'transaction'; transactionId: string }
	| { type: 'batch'; batchId: string }
	| { type: 'multiple'; transactionIds: string[] };

const getSigningPaths = (target: SigningTarget) => {
	if (target.type === 'batch') {
		return {
			initiate: `transactions/batch/${target.batchId}/initiate-signing`,
			sign: `transactions/batch/${target.batchId}/sign`,
		};
	}
	if (target.type === 'multiple') {
		return {
			initiate: 'transactions/initiate-signing/multiple',
			sign: 'transactions/sign/multiple',
		};
	}

	return {
		initiate: `transactions/${target.transactionId}/initiate-signing`,
		sign: `transactions/${target.transactionId}/sign`,
	};
};

const useSigningRequest = () => {
	const { selectedProfile } = useBankProfile();

	return async (path: string, body: Record<string, unknown> | null) => {
		const response = await apiClient.patch(
			`/profiles/${selectedProfile.id}/${path}`,
			body,
		);

		return response.data;
	};
};

const useInitiatePaymentSigning = () => {
	const patchSigning = useSigningRequest();

	return useMutation({
		mutationFn: (target: SigningTarget) =>
			patchSigning(
				getSigningPaths(target).initiate,
				target.type === 'multiple' ? { transaction_uuids: target.transactionIds } : null,
			),
	});
};

const useSignPayment = () => {
	const patchSigning = useSigningRequest();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ code, target }: { target: SigningTarget; code: string }) =>
			patchSigning(getSigningPaths(target).sign, {
				code,
				...(target.type === 'multiple' && {
					transaction_uuids: target.transactionIds,
				}),
			}),
		onSuccess: async (data, { target }) => {
			if (target.type === 'batch') {
				await queryClient.invalidateQueries({ queryKey: ['batch-transactions'] });
			}

			return data;
		},
	});
};

export type { SigningTarget };
export { useInitiatePaymentSigning, useSignPayment };
